/**
 * REVIVE Production Client-Side API Service
 * Communicates with backend endpoints under /api
 */

export interface ApiUser {
  id: string;
  email: string;
  displayName: string;
  role: 'USER' | 'ADMIN' | 'SUPER_ADMIN';
  entitlements: string[];
}

export interface ApiSound {
  id: string;
  slug: string;
  name: string;
  category: 'ICE' | 'EV';
  tagline: string;
  isFreeGuest: boolean;
  isUnlocked: boolean;
}

export interface ApiPackage {
  id: string;
  slug: string;
  name: string;
  description: string;
  price_usd: number;
  currency: string;
}

export interface ApiTerms {
  version: string;
  title: string;
  content: string;
  published_at?: string;
}

const API_BASE = '/api';

async function fetchJson<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    credentials: 'include', // sends/receives HttpOnly session cookies
  });

  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text || `HTTP ${res.status}: ${res.statusText}` };
    }
  } else {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Request failed with status ${res.status}`);
  }
  return data as T;
}

export const api = {
  auth: {
    async register(email: string, password: string, displayName?: string): Promise<{ user: ApiUser }> {
      return fetchJson('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName }),
      });
    },

    async login(email: string, password: string): Promise<{ user: ApiUser }> {
      return fetchJson('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
    },

    async logout(): Promise<void> {
      await fetchJson('/auth/logout', { method: 'POST' });
    },

    async getMe(): Promise<{ isAuthenticated: boolean; isGuest: boolean; user: ApiUser | null }> {
      try {
        return await fetchJson('/auth/me');
      } catch {
        return { isAuthenticated: false, isGuest: true, user: null };
      }
    },

    async forgotPassword(email: string): Promise<{ message: string }> {
      return fetchJson('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
  },

  sounds: {
    async getCatalogue(): Promise<{ sounds: ApiSound[]; userEntitlements: string[]; isAuthenticated: boolean }> {
      return fetchJson('/sounds');
    },

    async verifyAccess(slug: string): Promise<{ isAllowed: boolean; reason: string; requiredPackage?: string }> {
      return fetchJson(`/sounds/${slug}/verify`);
    },
  },

  packages: {
    async getPackages(): Promise<{ packages: ApiPackage[] }> {
      return fetchJson('/packages');
    },
  },

  payments: {
    async createPayment(packageId: string): Promise<{ status: string; message: string }> {
      return fetchJson('/payments/create', {
        method: 'POST',
        body: JSON.stringify({ packageId }),
      });
    },
  },

  terms: {
    async getCurrentTerms(): Promise<{ terms: ApiTerms }> {
      return fetchJson('/terms/current');
    },

    async acceptTerms(termsVersion: string = '1.0'): Promise<void> {
      await fetchJson('/terms/accept', {
        method: 'POST',
        body: JSON.stringify({ termsVersion }),
      });
    },

    async acknowledgeSafety(version: string = '1.0'): Promise<void> {
      await fetchJson('/terms/safety/acknowledge', {
        method: 'POST',
        body: JSON.stringify({ version }),
      });
    },
  },

  admin: {
    async getUsers(): Promise<{ users: any[] }> {
      return fetchJson('/admin/users');
    },

    async getAuditLogs(): Promise<{ logs: any[] }> {
      return fetchJson('/admin/audit-logs');
    },

    async toggleFreeSound(soundId: string, isFreeGuest: boolean): Promise<any> {
      return fetchJson('/admin/sounds/toggle-free', {
        method: 'POST',
        body: JSON.stringify({ soundId, isFreeGuest }),
      });
    },

    async authorizeSimulation(): Promise<{ isAuthorized: boolean; simulationToken: string }> {
      return fetchJson('/admin/simulation-auth', {
        method: 'POST',
      });
    },
  },
};
